import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import logo from "assets/images/logo.png";
import { addressLines } from "pages/Sales/SplitShipments/splitAddress";

// Packing slip for one split shipment (user, 2026-11-04): who it goes to,
// how, and exactly which products / sizes / qty are in THIS box -- with each
// product's image. No prices.

const s = StyleSheet.create({
  page: { padding: 24, fontSize: 10, color: "#222" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  logo: { width: 130, height: 55, objectFit: "contain" },
  title: { fontSize: 20, fontWeight: "bold", textAlign: "right" },
  muted: { color: "#666" },
  right: { textAlign: "right" },
  boxes: { flexDirection: "row", gap: 8, marginBottom: 12 },
  box: { flex: 1, border: "1px solid #ccc", borderRadius: 4, padding: 8 },
  boxTitle: {
    fontSize: 9,
    color: "#666",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  bold: { fontWeight: "bold" },
  row: { flexDirection: "row", marginBottom: 2 },
  key: { width: 70, color: "#666" },
  product: {
    flexDirection: "row",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: 8,
    marginBottom: 6,
  },
  image: {
    width: 110,
    height: 85,
    marginRight: 10,
    backgroundColor: "#f3f3f3",
    objectFit: "contain",
  },
  chips: { flexDirection: "row", flexWrap: "wrap", marginTop: 6 },
  chip: {
    border: "1px solid #888",
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 4,
    marginBottom: 4,
    fontSize: 9,
  },
  total: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 4,
    fontSize: 12,
    fontWeight: "bold",
  },
  notes: { border: "1px solid #ccc", borderRadius: 4, padding: 8, marginTop: 8 },
  footer: {
    position: "absolute",
    bottom: 14,
    left: 24,
    right: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: "#888",
  },
});

const today = () =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

/**
 * @param split      { splitId, shippingAddress (object), carrier, trackingNo,
 *                     status, notes, shipDate }
 * @param workOrder  { woId, jobName, customerName }
 * @param products   [{ id, productType, productStyle, productColor,
 *                      productDescription, image (data url), sizes: [{size, qty}] }]
 *                   -- only what is in this split
 * @param locationLogo  data url or null
 */
export function SplitShipmentPdf({ split, workOrder, products, locationLogo }) {
  const totalQty = products.reduce(
    (sum, p) => sum + p.sizes.reduce((a, z) => a + z.qty, 0),
    0,
  );
  const shipTo = addressLines(split.shippingAddress);

  return (
    <Document
      title={`Packing-Slip-SP-${split.splitId}`}
      author="OrderRun-SplitShipment"
    >
      <Page size="LETTER" style={s.page}>
        <View style={s.header}>
          <Image src={locationLogo || logo} style={s.logo} />
          <View>
            <Text style={s.title}>Packing Slip</Text>
            <Text style={[s.right, s.bold, { fontSize: 13 }]}>
              SP-{split.splitId}
            </Text>
            <Text style={[s.right, s.muted]}>W-{workOrder.woId}</Text>
            <Text style={[s.right, s.muted]}>Printed {today()}</Text>
          </View>
        </View>

        <View style={s.boxes}>
          <View style={s.box}>
            <Text style={s.boxTitle}>Ship to</Text>
            {shipTo.length ? (
              shipTo.map((line, i) => (
                <Text key={i} style={i === 0 ? s.bold : undefined}>
                  {line}
                </Text>
              ))
            ) : (
              <Text style={s.muted}>No address</Text>
            )}
          </View>
          <View style={s.box}>
            <Text style={s.boxTitle}>Order</Text>
            <View style={s.row}>
              <Text style={s.key}>Customer</Text>
              <Text>{workOrder.customerName || "-"}</Text>
            </View>
            <View style={s.row}>
              <Text style={s.key}>Job</Text>
              <Text>{workOrder.jobName || "-"}</Text>
            </View>
            <View style={s.row}>
              <Text style={s.key}>Carrier</Text>
              <Text>{split.carrier || "-"}</Text>
            </View>
            <View style={s.row}>
              <Text style={s.key}>Tracking</Text>
              <Text>{split.trackingNo || "-"}</Text>
            </View>
            <View style={s.row}>
              <Text style={s.key}>Ship date</Text>
              <Text>{split.shipDate || "-"}</Text>
            </View>
            <View style={s.row}>
              <Text style={s.key}>Pieces</Text>
              <Text style={s.bold}>{totalQty}</Text>
            </View>
          </View>
        </View>

        {products.map((p) => {
          const qty = p.sizes.reduce((a, z) => a + z.qty, 0);
          return (
            <View key={p.id} style={s.product} wrap={false}>
              {p.image ? (
                <Image src={p.image} style={s.image} />
              ) : (
                <View style={s.image} />
              )}
              <View style={{ flex: 1 }}>
                <Text style={s.bold}>
                  {[p.productType, p.productStyle].filter(Boolean).join(" - ")}
                </Text>
                {p.productColor ? <Text>{p.productColor}</Text> : null}
                {p.productDescription ? (
                  <Text style={s.muted}>{p.productDescription}</Text>
                ) : null}
                <View style={s.chips}>
                  {p.sizes.map((z) => (
                    <Text key={z.size} style={s.chip}>
                      {z.size}: {z.qty}
                    </Text>
                  ))}
                </View>
              </View>
              <Text style={[s.bold, { fontSize: 12, width: 50, textAlign: "right" }]}>
                {qty}
              </Text>
            </View>
          );
        })}

        <View style={s.total}>
          <Text>Total pieces: {totalQty}</Text>
        </View>

        {split.notes ? (
          <View style={s.notes} wrap={false}>
            <Text style={s.boxTitle}>Notes</Text>
            <Text>{split.notes}</Text>
          </View>
        ) : null}

        <View style={s.footer} fixed>
          <Text>
            SP-{split.splitId} / W-{workOrder.woId}
          </Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
